import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject, Subscription, debounceTime, distinctUntilChanged, switchMap, catchError, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocationUtils } from 'src/app/utils';
import { GoogleMapLoaderService } from 'src/app/services/google-map-loader.service';
import { PlaceSuggestion, SelectedPlace } from './interface';



@Component({
    selector: 'app-location-autocomplete',
    templateUrl: './location-autocomplete.component.html',
    styleUrls: ['./location-autocomplete.component.scss']
})
export class LocationAutocompleteComponent
    implements OnInit, OnDestroy, OnChanges {

    @ViewChild('locationInput')
    locationInput!: ElementRef<HTMLInputElement>;

    @Input() placeholder = 'Enter a location';

    @Input() initialValue: string | null = null;

    @Input() initialLocation: any = null;

    @Input() includedRegionCodes: string[] = ['us'];

    @Output() placeSelected =
        new EventEmitter<SelectedPlace>();
    @Output() loadError =
        new EventEmitter<Error>();
    @Output() currentLocationSelected =
        new EventEmitter<SelectedPlace>();
    private searchSubject = new Subject<string>();
    private searchSubscription?: Subscription;
    suggestions: PlaceSuggestion[] = [];
    value = '';
    isLoading = false;
    isFocused = false;

    selectedSuggestionIndex = -1;

    private readonly apiUrl =
        'https://places.googleapis.com/v1/places:autocomplete';

    private apiKey = environment.googleMapsApiKey

    constructor(
        private http: HttpClient,
        private locationUtils: LocationUtils,
        private googleMapLoaderService: GoogleMapLoaderService
    ) { }

    async ngOnInit(): Promise<void> {
        try {
            await this.googleMapLoaderService.load();
            this.value =
                this.initialValue ??
                this.initialLocation?.label ??
                '';
            this.searchSubscription =
                this.searchSubject
                    .pipe(
                        debounceTime(300),
                        distinctUntilChanged(),

                        switchMap(value => {

                            if (!value || value.trim().length < 2) {
                                this.suggestions = [];
                                return of(null);
                            }
                            this.isLoading = true;
                            return this.getSuggestions(value)
                                .pipe(
                                    catchError(error => {

                                        console.error(
                                            'Location autocomplete error',
                                            error
                                        );

                                        this.loadError.emit(
                                            error instanceof Error
                                                ? error
                                                : new Error(
                                                    'Unable to search locations'
                                                )
                                        );

                                        return of(null);
                                    })
                                );
                        })
                    )
                    .subscribe(response => {

                        this.isLoading = false;

                        if (!response) {
                            return;
                        }

                        this.suggestions =
                            response.suggestions ?? [];

                        this.selectedSuggestionIndex = -1;
                    });

        } catch (error) {
            this.isLoading = false;
            this.loadError.emit(error as Error);
        }

    }

    ngOnChanges(changes: SimpleChanges): void {

        if (
            changes['initialValue'] &&
            !changes['initialValue'].firstChange
        ) {
            this.value =
                this.initialValue ?? '';
        }

        if (
            changes['initialLocation'] &&
            !changes['initialLocation'].firstChange
        ) {
            this.value =
                this.initialLocation?.label ??
                this.initialValue ??
                '';
        }
    }

    onInput(event: Event): void {

        const input =
            event.target as HTMLInputElement;

        this.value = input.value;

        this.searchSubject.next(
            this.value.trim()
        );
    }

    onFocus(): void {

        this.isFocused = true;

        if (this.value.trim().length >= 2) {
            this.searchSubject.next(
                this.value.trim()
            );
        }
    }

    onBlur(): void {

        /**
         * Delay hiding suggestions so that
         * click on a suggestion can execute.
         */
        setTimeout(() => {
            this.isFocused = false;
        }, 200);
    }

    selectSuggestion(
        suggestion: PlaceSuggestion
    ): void {

        const prediction =
            suggestion.placePrediction;

        const placeId =
            prediction.placeId;

        this.value =
            prediction.text.text;

        this.suggestions = [];

        this.selectedSuggestionIndex = -1;

        this.getPlaceDetails(placeId);
    }

    private getSuggestions(value: string) {


        const headers =
            new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': this.apiKey
            });

        const body: any = {
            input: value,

            includedRegionCodes:
                this.includedRegionCodes
        };

        return this.http.post<any>(
            this.apiUrl,
            body,
            { headers }
        );
    }

    private getPlaceDetails(
        placeId: string
    ): void {
        this.isLoading = true;
        const headers =
            new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': this.apiKey,
                'X-Goog-FieldMask':
                    'id,displayName,formattedAddress,location,addressComponents'
            });

        this.http
            .get<any>(
                `https://places.googleapis.com/v1/places/${placeId}`,
                { headers }
            )
            .subscribe({

                next: place => {
                    this.isLoading = false;
                    const selectedPlace: SelectedPlace = {
                        placeId: place.id,

                        displayName:
                            place.displayName?.text,
                        formattedAddress:
                            place.formattedAddress,
                        location: place.location
                            ? {
                                lat: place.location.latitude,
                                lng: place.location.longitude
                            }
                            : undefined,
                        addressComponents:
                            place.addressComponents,
                        raw: place
                    };

                    this.value =
                        selectedPlace.formattedAddress ??
                        selectedPlace.displayName ??
                        this.value;

                    this.placeSelected.emit(
                        selectedPlace
                    );
                },

                error: error => {

                    this.isLoading = false;

                    console.error(
                        'Place details error',
                        error
                    );

                    this.loadError.emit(
                        error instanceof Error
                            ? error
                            : new Error(
                                'Unable to load place details'
                            )
                    );
                }
            });
    }

    onKeyDown(event: KeyboardEvent): void {

        if (!this.suggestions.length) {
            return;
        }

        switch (event.key) {

            case 'ArrowDown':

                event.preventDefault();

                this.selectedSuggestionIndex =
                    Math.min(
                        this.selectedSuggestionIndex + 1,
                        this.suggestions.length - 1
                    );

                break;

            case 'ArrowUp':

                event.preventDefault();

                this.selectedSuggestionIndex =
                    Math.max(
                        this.selectedSuggestionIndex - 1,
                        0
                    );

                break;

            case 'Enter':

                event.preventDefault();

                if (
                    this.selectedSuggestionIndex >= 0
                ) {

                    this.selectSuggestion(
                        this.suggestions[
                        this.selectedSuggestionIndex
                        ]
                    );
                }

                break;

            case 'Escape':

                this.suggestions = [];

                this.selectedSuggestionIndex = -1;

                break;
        }
    }

    clear(): void {

        this.value = '';

        this.suggestions = [];

        this.selectedSuggestionIndex = -1;

        this.locationInput
            ?.nativeElement
            .focus();

        /**
         * Emit empty location if parent
         * needs to clear its form value.
         */
        this.placeSelected.emit({
            raw: null
        });
    }

    ngOnDestroy(): void {

        this.searchSubscription?.unsubscribe();

        this.searchSubject.complete();
    }

    async handleSelectCurrentLocationClick(): Promise<void> {

        if (!navigator.geolocation) {
            this.loadError.emit(
                new Error('Geolocation is not supported by this browser.')
            );

            return;
        }

        this.isLoading = true;

        navigator.geolocation.getCurrentPosition(

            async (position) => {

                try {

                    const loc: any =
                        await this.locationUtils.getLocationDetails(
                            position
                        );

                    if (!loc) {
                        this.isLoading = false;
                        this.loadError.emit(
                            new Error(
                                'Unable to determine your current location.'
                            )
                        );

                        return;
                    }

                    const selectedPlace: SelectedPlace = {
                        displayName:
                            loc.displayName,
                        formattedAddress:
                            loc.displayName,
                        location: {
                            lat: loc.lat,
                            lng: loc.lng
                        },
                        addressComponents: [],
                        raw: loc
                    };

                    this.value =
                        loc.displayName;
                    this.suggestions = [];
                    this.selectedSuggestionIndex = -1;
                    this.currentLocationSelected.emit(
                        selectedPlace
                    );

                } catch (error) {

                    console.error(
                        'Current location error:',
                        error
                    );

                    this.loadError.emit(
                        error instanceof Error
                            ? error
                            : new Error(
                                'Unable to determine current location.'
                            )
                    );

                } finally {

                    this.isLoading = false;
                }
            },

            (error) => {
                console.error(
                    'Geolocation error:',
                    error.message
                );
                this.isLoading = false;

                this.loadError.emit(
                    new Error(
                        this.getGeolocationErrorMessage(error)
                    )
                );
            },

            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000
            }
        );
    }

    private getGeolocationErrorMessage(
        error: GeolocationPositionError
    ): string {

        switch (error.code) {

            case error.PERMISSION_DENIED:
                return 'Location permission was denied.';

            case error.POSITION_UNAVAILABLE:
                return 'Current location is unavailable.';

            case error.TIMEOUT:
                return 'Unable to get current location.';

            default:
                return 'Unable to determine current location.';
        }
    }
}